import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../api'
import { t } from '../i18n'
import { haptic, notify, useBackButton } from '../telegram'
import type { AdminPlayer } from '../types'

interface Props {
  onClose: () => void
}

function initials(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '?'
}

function parseLevel(raw: string): number | null {
  const value = Number(raw.replace(',', '.').trim())
  return raw.trim() !== '' && Number.isFinite(value) && value >= 0 && value <= 7 ? value : null
}

/** Единственный экран администратора: список игроков и правка уровня.
 *  Сервер проверяет права сам — экран просто не показывается остальным. */
export default function AdminPlayers({ onClose }: Props) {
  const [q, setQ] = useState('')
  const [people, setPeople] = useState<AdminPlayer[] | null>(null)
  const [openId, setOpenId] = useState<number | null>(null)
  const [draft, setDraft] = useState('')
  const [reset, setReset] = useState(false)
  const [saving, setSaving] = useState(false)
  const request = useRef(0)

  useEffect(() => useBackButton(onClose), [onClose])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const load = useCallback(async (query: string) => {
    const id = ++request.current
    try {
      const found = await api.adminPlayers(query)
      if (id === request.current) setPeople(found)
    } catch (e) {
      if (id === request.current) {
        setPeople([])
        notify(e instanceof Error ? e.message : 'Ошибка')
      }
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => void load(q.trim()), q ? 250 : 0)
    return () => clearTimeout(timer)
  }, [q, load])

  function open(p: AdminPlayer) {
    if (openId === p.id) return setOpenId(null)
    setOpenId(p.id)
    setDraft(p.level != null ? p.level.toFixed(2) : '')
    setReset(false)
  }

  async function save(p: AdminPlayer) {
    const level = parseLevel(draft)
    if (level === null) return notify(t().onbLevelError)
    setSaving(true)
    try {
      const updated = await api.adminSetLevel(p.id, level, reset)
      setPeople((list) => (list ?? []).map((x) => (x.id === p.id ? updated : x)))
      setOpenId(null)
      haptic('medium')
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSaving(false)
    }
  }

  async function organizer(p: AdminPlayer, action: 'approve' | 'reject' | 'revoke') {
    setSaving(true)
    try {
      const updated = await api.adminOrganizer(p.id, action)
      setPeople((list) => (list ?? []).map((x) => (x.id === p.id ? updated : x)))
      haptic('medium')
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={t().adminPlayers}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={onClose} aria-label={t().doneClose}>
          ←
        </button>
        <div style={{ minWidth: 0 }}>
          <div className="title chat-title">{t().adminPlayers}</div>
          <div className="muted small">{people ? people.length : ''}</div>
        </div>
      </div>

      <div className="chat-list">
        <div className="muted small" style={{ marginBottom: 8 }}>
          {t().adminHint}
        </div>
        <input
          type="search"
          placeholder={t().adminSearch}
          aria-label={t().adminSearch}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        {people === null && <p className="muted">{t().loading}</p>}
        {people?.length === 0 && <p className="muted">{t().adminEmpty}</p>}

        {people?.map((p) => (
          <div key={p.id} className="admin-row">
            <button className="admin-head" aria-expanded={openId === p.id} onClick={() => open(p)}>
              {p.photo_url ? (
                <img className="avatar" style={{ marginLeft: 0 }} src={p.photo_url} alt="" />
              ) : (
                <span className="avatar" style={{ marginLeft: 0 }}>
                  {initials(p.first_name)}
                </span>
              )}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 600, display: 'block' }}>
                  {p.first_name}
                  {p.username && <span className="muted"> @{p.username}</span>}
                </span>
                <span className="muted small" style={{ display: 'block' }}>
                  {t().adminSource(p.level_source)} · {t().adminMatches(p.rated_matches)}
                </span>
              </span>
              {p.organizer_status === 'pending' && <span className="badge warn">🏆</span>}
              {p.organizer_status === 'approved' && <span className="badge">🏆</span>}
              <span className={p.level != null ? 'admin-level' : 'badge warn'}>
                {p.level != null ? p.level.toFixed(2) : t().adminNoLevel}
              </span>
            </button>

            {openId === p.id && (
              <div className="admin-edit">
                <label style={{ marginTop: 0 }}>{t().adminNewLevel}</label>
                <div className="row" style={{ gap: 8 }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="level-input"
                    value={draft}
                    maxLength={4}
                    aria-label={t().adminNewLevel}
                    onChange={(e) => setDraft(e.target.value.replace(/[^\d.,]/g, ''))}
                  />
                  <button style={{ flex: '0 0 auto' }} disabled={saving} onClick={() => save(p)}>
                    {t().save}
                  </button>
                </div>
                <label>
                  <span className="row" style={{ gap: 10 }}>
                    <input
                      type="checkbox"
                      style={{ width: 20, flex: '0 0 auto' }}
                      checked={reset}
                      onChange={(e) => setReset(e.target.checked)}
                    />
                    {t().adminReset}
                  </span>
                </label>
                <label>{t().orgSection}</label>
                {p.organizer_status === 'pending' && (
                  <div className="muted small" style={{ marginBottom: 6 }}>
                    {t().orgBadgePending}
                  </div>
                )}
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {p.organizer_status !== 'approved' && (
                    <button style={{ flex: 1 }} disabled={saving} onClick={() => organizer(p, 'approve')}>
                      {t().orgApprove}
                    </button>
                  )}
                  {p.organizer_status === 'pending' && (
                    <button className="ghost" style={{ flex: 1 }} disabled={saving} onClick={() => organizer(p, 'reject')}>
                      {t().orgReject}
                    </button>
                  )}
                  {p.organizer_status === 'approved' && (
                    <button className="ghost" style={{ flex: 1 }} disabled={saving} onClick={() => organizer(p, 'revoke')}>
                      {t().orgRevoke}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
